import { useState, useCallback, useEffect } from "react";
import { usePost } from "./usePost";
import { useJobPolling } from "./useJobPolling";
import { useToast } from "../store/ToastProvider";
import { AIModalState } from "../enums/CompassAI";
import {
  AILinkageAnalysisRequest,
  AILinkageAnalysisResponse,
  AIBatchLinkageRequest,
  AIBatchLinkageResponse,
  AIFileDownloadRequest,
  AIJobProgress,
} from "../types/compassAITypes";
import { MESSAGES } from "../messages";

interface UseBatchLinkageAnalysisReturn {
  state: AIModalState;
  batchResult: AIBatchLinkageResponse | null;
  errorMessage: string | null;
  progress: AIJobProgress | null;
  startAnalysis: () => Promise<void>;
  downloadFile: (path: string) => Promise<string | null>;
  reset: () => void;
  isAnalyzing: boolean;
  isPolling: boolean;
  isComplete: boolean;
  hasError: boolean;
}

interface UseLinkageAnalysisReturn {
  state: AIModalState;
  analysisResult: AILinkageAnalysisResponse | null;
  errorMessage: string | null;
  analyzeCompany: (
    companyId: string,
    options?: {
      fundIds?: string[];
      managerIds?: string[];
      minConfidence?: number;
    },
  ) => Promise<boolean>;
  analyzeBatch: (options?: {
    fundIds?: string[];
    managerIds?: string[];
    minConfidence?: number;
    batchSize?: number;
    maxCompanies?: number;
  }) => Promise<boolean>;
  reset: () => void;
  isAnalyzing: boolean;
  isComplete: boolean;
  hasError: boolean;
}

export function useBatchLinkageAnalysis(): UseBatchLinkageAnalysisReturn {
  const { addToast } = useToast();

  const [state, setState] = useState<AIModalState>(AIModalState.IDLE);
  const [batchResult, setBatchResult] = useState<AIBatchLinkageResponse | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { sendData: getDownloadUrl } = usePost<
    { url: string },
    AIFileDownloadRequest
  >();

  const {
    startJob,
    reset: resetPolling,
    progress,
    result: jobResult,
    errorMessage: jobError,
    isPolling,
    isComplete: jobComplete,
    isFailed: jobFailed,
  } = useJobPolling<AIBatchLinkageResponse>();

  useEffect(() => {
    if (isPolling && state !== AIModalState.POLLING) {
      setState(AIModalState.POLLING);
    }
  }, [isPolling]);

  useEffect(() => {
    if (jobComplete && jobResult) {
      setBatchResult(jobResult);
      setState(AIModalState.SUCCESS);
    }
  }, [jobComplete, jobResult]);

  useEffect(() => {
    if (jobFailed) {
      setState(AIModalState.ERROR);
      const msg = jobError || MESSAGES.COMPASS_AI.ERRORS.ANALYSIS_FAILED;
      setErrorMessage(msg);
      addToast(MESSAGES.COMPASS_AI.ERRORS.ANALYSIS_FAILED, "error");
    }
  }, [jobFailed]);

  const startAnalysis = useCallback(async (): Promise<void> => {
    setState(AIModalState.ANALYZING);
    setErrorMessage(null);

    const request: AIBatchLinkageRequest = {
      min_confidence: 0.6,
      company_batch_size: 25,
      max_companies: 500,
    };

    await startJob(
      "AI_BATCH_LINKAGE",
      request as unknown as Record<string, unknown>,
    );
  }, [startJob]);

  const downloadFile = useCallback(
    async (path: string): Promise<string | null> => {
      const response = await getDownloadUrl("FILE_DOWNLOAD", { path });

      if (response?.url) {
        return response.url;
      } else {
        addToast(MESSAGES.COMPASS_AI.ERRORS.DOWNLOAD_FAILED, "error");
        return null;
      }
    },
    [getDownloadUrl, addToast],
  );

  const reset = useCallback((): void => {
    setState(AIModalState.IDLE);
    setBatchResult(null);
    setErrorMessage(null);
    resetPolling();
  }, [resetPolling]);

  return {
    state,
    batchResult,
    errorMessage,
    progress,
    startAnalysis,
    downloadFile,
    reset,
    isAnalyzing:
      state === AIModalState.ANALYZING || state === AIModalState.POLLING,
    isPolling: state === AIModalState.POLLING,
    isComplete: state === AIModalState.SUCCESS,
    hasError: state === AIModalState.ERROR,
  };
}

export function useLinkageAnalysis(): UseLinkageAnalysisReturn {
  const { addToast } = useToast();

  const [state, setState] = useState<AIModalState>(AIModalState.IDLE);
  const [analysisResult, setAnalysisResult] =
    useState<AILinkageAnalysisResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { sendData: triggerSingleAnalysis, error: singleAnalysisError } =
    usePost<AILinkageAnalysisResponse, AILinkageAnalysisRequest>();

  const { sendData: triggerBatchAnalysis, error: batchAnalysisError } = usePost<
    AILinkageAnalysisResponse,
    AIBatchLinkageRequest
  >();

  const analyzeCompany = useCallback(
    async (
      companyId: string,
      options?: {
        fundIds?: string[];
        managerIds?: string[];
        minConfidence?: number;
      },
    ): Promise<boolean> => {
      if (!companyId) {
        setErrorMessage("Company ID is required");
        return false;
      }

      setState(AIModalState.ANALYZING);
      setErrorMessage(null);

      const request: AILinkageAnalysisRequest = {
        compass_cid: companyId,
        fund_id: options?.fundIds,
        manager_id: options?.managerIds,
        min_confidence: options?.minConfidence ?? 0.3,
      };

      const result = await triggerSingleAnalysis("AI_ANALYZE_LINKAGE", request);

      if (result && !singleAnalysisError) {
        setAnalysisResult(result);
        setState(AIModalState.SUCCESS);
        return true;
      } else {
        setState(AIModalState.ERROR);
        setErrorMessage(MESSAGES.COMPASS_AI.ERRORS.ANALYSIS_FAILED);
        addToast(MESSAGES.COMPASS_AI.ERRORS.ANALYSIS_FAILED, "error");
        return false;
      }
    },
    [triggerSingleAnalysis, singleAnalysisError, addToast],
  );

  const analyzeBatch = useCallback(
    async (options?: {
      fundIds?: string[];
      managerIds?: string[];
      minConfidence?: number;
      batchSize?: number;
      maxCompanies?: number;
    }): Promise<boolean> => {
      setState(AIModalState.ANALYZING);
      setErrorMessage(null);

      const request: AIBatchLinkageRequest = {
        fund_id: options?.fundIds,
        manager_id: options?.managerIds,
        min_confidence: options?.minConfidence ?? 0.3,
        company_batch_size: options?.batchSize ?? 25,
        max_companies: options?.maxCompanies ?? 100,
      };

      const result = await triggerBatchAnalysis("AI_BATCH_LINKAGE", request);

      if (result && !batchAnalysisError) {
        setAnalysisResult(result);
        setState(AIModalState.SUCCESS);
        return true;
      } else {
        setState(AIModalState.ERROR);
        setErrorMessage(MESSAGES.COMPASS_AI.ERRORS.ANALYSIS_FAILED);
        addToast(MESSAGES.COMPASS_AI.ERRORS.ANALYSIS_FAILED, "error");
        return false;
      }
    },
    [triggerBatchAnalysis, batchAnalysisError, addToast],
  );

  const reset = useCallback((): void => {
    setState(AIModalState.IDLE);
    setAnalysisResult(null);
    setErrorMessage(null);
  }, []);

  return {
    state,
    analysisResult,
    errorMessage,
    analyzeCompany,
    analyzeBatch,
    reset,
    isAnalyzing: state === AIModalState.ANALYZING,
    isComplete: state === AIModalState.SUCCESS,
    hasError: state === AIModalState.ERROR,
  };
}
